import type { ContentRegistry } from '../content/registry';
import { paradoxDef } from '../content/paradox';
import type { ItemDef, StatMod } from '../content/types';
import { metaCost, shardReward, runScore } from '../sim/formulas';
import type { RunConfig } from '../sim/sim';
import type { Sim } from '../sim/sim';
import type { SaveData } from './save';
import { Rng } from '../core/rng';
import { CHARACTERS, DEFAULT_CHARACTER, characterDef, type CharacterId } from '../content/characters';
import { dailySeed } from './backend';
import { assignedPetBonuses, PETS, PET_ASSIGNMENT_STATIONS, PET_BY_ID, type PetAssignmentStation } from '../content/pets';
import { CHALLENGE_BY_ID, challengeCompleted, type ChallengeDef, type ChallengeMetric } from '../content/challenges';
import { canAllocateSkill, resolveSkillBuild, type SkillFlag } from '../content/skills';

/**
 * Profile: pure functions over SaveData + ContentRegistry. The hub UI calls
 * these; nothing here touches storage or the DOM.
 */

export function metaMods(save: SaveData, reg: ContentRegistry): StatMod[] {
  void reg;
  return skillModsForCharacter(save, characterDef(save.selectedCharacter).id);
}

function skillModsForCharacter(save: SaveData, character: CharacterId): StatMod[] {
  const progress = save.characters[character];
  return resolveSkillBuild(character, progress?.allocated, skillPointsForLevel(characterLevel(progress?.xp ?? 0))).mods;
}

function skillFlagsForCharacter(save: SaveData, character: CharacterId): SkillFlag[] {
  const progress = save.characters[character];
  return resolveSkillBuild(character, progress?.allocated, skillPointsForLevel(characterLevel(progress?.xp ?? 0))).flags;
}

export const MAX_CHARACTER_LEVEL = 30;
export const MAX_PET_LEVEL = 10;

export function characterXpToNext(level: number): number { return Math.round(180 + 20 * level ** 1.45); }
export function characterLevel(xp: number): number {
  let level = 1;
  let remaining = Math.max(0, xp);
  while (level < MAX_CHARACTER_LEVEL && remaining >= characterXpToNext(level)) remaining -= characterXpToNext(level++);
  return level;
}
export function characterLevelProgress(xp: number): { level: number; current: number; next: number } {
  const level = characterLevel(xp);
  let spent = 0;
  for (let l = 1; l < level; l++) spent += characterXpToNext(l);
  return { level, current: Math.max(0, xp - spent), next: level >= MAX_CHARACTER_LEVEL ? 0 : characterXpToNext(level) };
}
export function skillPointsForLevel(level: number): number { return Math.max(0, level - 1) + (level >= 10 ? 1 : 0) + (level >= 20 ? 1 : 0) + (level >= 30 ? 1 : 0); }
export function spentSkillPoints(save: SaveData, character: CharacterId): number {
  const progress = save.characters[character];
  return resolveSkillBuild(character, progress?.allocated, skillPointsForLevel(characterLevel(progress?.xp ?? 0))).spent;
}
export function allocateSkill(save: SaveData, character: CharacterId, nodeId: string): boolean {
  const progress = save.characters[character];
  const budget = skillPointsForLevel(characterLevel(progress.xp));
  if (!canAllocateSkill(character, progress.allocated, nodeId, budget)) return false;
  progress.allocated[nodeId] = (progress.allocated[nodeId] ?? 0) + 1;
  return true;
}
export function respecSkills(save: SaveData, character: CharacterId): void { save.characters[character].allocated = {}; }
export function petLevel(xp: number): number {
  let level = 1;
  let remaining = Math.max(0, xp);
  while (level < MAX_PET_LEVEL) { const next = Math.round(150 * level ** 1.5); if (remaining < next) break; remaining -= next; level++; }
  return level;
}

export function nextMetaCost(save: SaveData, reg: ContentRegistry, id: string): number | null {
  const def = reg.metaUpgrades.find((m) => m.id === id);
  if (!def) return null;
  const tier = save.metaUpgrades[id] ?? 0;
  if (tier >= def.tiers) return null;
  return metaCost(def.baseCost, def.costGrowth, tier);
}

export function buyMeta(save: SaveData, reg: ContentRegistry, id: string): boolean {
  const cost = nextMetaCost(save, reg, id);
  if (cost === null || save.chronoshards < cost) return false;
  save.chronoshards -= cost;
  save.metaUpgrades[id] = (save.metaUpgrades[id] ?? 0) + 1;
  return true;
}

export function buyUnlock(save: SaveData, reg: ContentRegistry, id: string): boolean {
  const def = reg.unlocks.find((u) => u.id === id);
  if (!def || save.unlocks.includes(id) || save.chronoshards < def.cost) return false;
  save.chronoshards -= def.cost;
  save.unlocks.push(id);
  if ((def.kind === 'weapon' || def.kind === 'item') && def.ref && !save.inventory.includes(def.ref)) save.inventory.push(def.ref);
  if (def.kind === 'paradox') save.paradoxUnlocked = Math.max(save.paradoxUnlocked, countUnlocks(save, reg, 'paradox'));
  return true;
}

export function buyPet(save: SaveData, id: string): boolean {
  const pet = PET_BY_ID.get(id);
  if (!pet || save.pets.owned.includes(id) || !canAcquirePet(save, id)) return false;
  if (pet.cost > 0) save.chronoshards -= pet.cost;
  save.pets.owned.push(id);
  save.pets.levels[id] = 1;
  save.pets.xp[id] = 0;
  if (!save.pets.active) save.pets.active = id;
  return true;
}

export function selectPet(save: SaveData, id: string | null): boolean {
  if (id !== null && !save.pets.owned.includes(id)) return false;
  if (id) for (const station of PET_ASSIGNMENT_STATIONS) if (save.pets.assignments[station.id] === id) save.pets.assignments[station.id] = null;
  save.pets.active = id;
  return true;
}

export function canAcquirePet(save: SaveData, id: string): boolean {
  const pet = PET_BY_ID.get(id);
  if (!pet || save.pets.owned.includes(id)) return false;
  if (!pet.unlock) return save.chronoshards >= pet.cost;
  if (pet.unlock.type === 'anchor') return save.anchors.includes(pet.unlock.era);
  const requiredLevel = pet.unlock.level;
  return save.mastery.paradoxWins.some((level) => level >= requiredLevel);
}

export function petAcquisitionLabel(save: SaveData, id: string): string {
  const pet = PET_BY_ID.get(id);
  if (!pet) return 'Unavailable';
  if (!pet.unlock) return `◆ ${pet.cost}`;
  if (canAcquirePet(save, id)) return 'Claim earned companion';
  return pet.unlock.type === 'anchor' ? `Secure Era ${pet.unlock.era + 1} Anchor` : `Complete a full Paradox ${pet.unlock.level} run`;
}

export function assignPet(save: SaveData, station: PetAssignmentStation, id: string | null): boolean {
  if (!PET_ASSIGNMENT_STATIONS.some((entry) => entry.id === station)) return false;
  if (id !== null && (!save.pets.owned.includes(id) || id === save.pets.active)) return false;
  if (id) for (const entry of PET_ASSIGNMENT_STATIONS) if (save.pets.assignments[entry.id] === id) save.pets.assignments[entry.id] = null;
  save.pets.assignments[station] = id;
  return true;
}

export function assignedPets(save: SaveData): string[] {
  return PET_ASSIGNMENT_STATIONS.map((station) => save.pets.assignments[station.id]).filter((id): id is string => Boolean(id));
}

function claimEarnedPets(save: SaveData): string[] {
  const claimed: string[] = [];
  for (const pet of PETS) {
    if (!pet.unlock || save.pets.owned.includes(pet.id) || !canAcquirePet(save, pet.id)) continue;
    save.pets.owned.push(pet.id);
    save.pets.levels[pet.id] = 1;
    save.pets.xp[pet.id] = 0;
    claimed.push(pet.id);
  }
  return claimed;
}

export function countUnlocks(save: SaveData, reg: ContentRegistry, kind: string): number {
  return reg.unlocks.filter((u) => u.kind === kind && save.unlocks.includes(u.id)).length;
}

export function equippedItems(save: SaveData, reg: ContentRegistry): ItemDef[] {
  const ids = [save.equipment.weapon, save.equipment.armor, save.equipment.relic];
  const out: ItemDef[] = [];
  for (const id of ids) {
    if (!id) continue;
    const it = reg.items.get(id);
    if (it && save.inventory.includes(id)) out.push(it);
  }
  if (!out.some((i) => i.slot === 'weapon')) {
    const starter = reg.items.get('w_spore_launcher');
    if (starter) out.unshift(starter);
  }
  return out;
}

export function equip(save: SaveData, reg: ContentRegistry, itemId: string | null, slot: 'weapon' | 'armor' | 'relic'): boolean {
  if (itemId === null) {
    if (slot === 'weapon') return false;
    save.equipment[slot] = null;
    return true;
  }
  const it = reg.items.get(itemId);
  if (!it || it.slot !== slot || !save.inventory.includes(itemId)) return false;
  save.equipment[slot] = itemId;
  return true;
}

export function unlockedUpgradeTags(save: SaveData, reg: ContentRegistry): Set<string> {
  const s = new Set<string>();
  for (const u of reg.unlocks) if (u.kind === 'upgradePool' && u.ref && save.unlocks.includes(u.id)) s.add(u.ref);
  return s;
}

export interface RunOptions {
  seed: number;
  players: number;
  startEra: number;
  paradox: number;
}

export function charactersForRun(save: SaveData, players: number): CharacterId[] {
  const unlocked = CHARACTERS.filter((c) => save.unlockedCharacters.includes(c.id));
  const selected = characterDef(save.selectedCharacter);
  const start = Math.max(0, unlocked.findIndex((c) => c.id === selected.id));
  return Array.from({ length: players }, (_, i) => unlocked[(start + i) % unlocked.length]?.id ?? DEFAULT_CHARACTER);
}

export function buildRunConfig(save: SaveData, reg: ContentRegistry, opts: RunOptions): RunConfig {
  const equipment = equippedItems(save, reg);
  const characters = charactersForRun(save, opts.players);
  const housePetBonus = assignedPetBonuses(assignedPets(save));
  const mods = characters.map((character) => [...skillModsForCharacter(save, character), ...housePetBonus.mods]);
  const skillFlags = characters.map((character) => skillFlagsForCharacter(save, character));
  return {
    seed: opts.seed,
    playerCount: opts.players,
    startEra: Math.min(opts.startEra, Math.max(0, ...save.anchors.map((a) => a + 1), 0)),
    paradox: Math.min(opts.paradox, save.paradoxUnlocked),
    metaMods: mods[0] ?? [],
    metaModsByPlayer: mods,
    skillFlags,
    equipment: Array.from({ length: opts.players }, () => equipment),
    characters,
    rerolls: countUnlocks(save, reg, 'reroll') + Math.max(0, Math.min(3, save.house.rooms.chronolab ?? 0)) + housePetBonus.rerolls,
    banishes: countUnlocks(save, reg, 'banish') + housePetBonus.banishes,
    unlocked: unlockedUpgradeTags(save, reg),
    pets: Array.from({ length: opts.players }, () => save.pets.active),
    petLevels: Array.from({ length: opts.players }, () => save.pets.active ? petLevel(save.pets.xp[save.pets.active] ?? 0) : 1),
    startingHeals: Array.from({ length: opts.players }, () => Math.max(0, Math.min(4, (save.house.rooms.garden ?? 0) + housePetBonus.startingHeals))),
  };
}

/** Fair daily configuration: no profile power, equipment, roster, or utility-charge advantages. */
export function buildDailyRunConfig(reg: ContentRegistry, key: string): RunConfig {
  const weapon = reg.items.get('w_spore_launcher');
  if (!weapon) throw new Error('Daily starter weapon is missing');
  return {
    seed: dailySeed(key),
    playerCount: 1,
    startEra: 0,
    paradox: 0,
    metaMods: [],
    equipment: [[weapon]],
    characters: [DEFAULT_CHARACTER],
    rerolls: 0,
    banishes: 0,
    unlocked: new Set(),
  };
}

/** Seeded challenges use fixed loadouts and no profile power, like daily runs. */
export function buildChallengeRunConfig(reg: ContentRegistry, challenge: ChallengeDef): RunConfig {
  const weapon = reg.items.get(challenge.weapon);
  if (!weapon) throw new Error(`Challenge weapon ${challenge.weapon} is missing`);
  return {
    seed: challenge.seed, playerCount: 1, startEra: challenge.startEra, paradox: challenge.paradox,
    metaMods: [], equipment: [[weapon]], characters: [challenge.character], rerolls: 0, banishes: 0,
    unlocked: new Set(), pets: [null], challengeId: challenge.id,
  };
}

export interface RunResult {
  shards: number;
  score: number;
  victory: boolean;
  erasCleared: number;
  bossesKilled: number;
  kills: number;
  level: number;
  timeSurvivedSec: number;
  highestEra: number;
  newAnchors: number[];
  /** Character ids unlocked by this run's newly secured anchors. */
  newCharacters: CharacterId[];
  /** Item ids awarded by era loot drops this run (see rollEraLoot). */
  drops: string[];
  /** Achievement ids first completed by this run. Filled after stats are applied. */
  newAchievements: string[];
  characterXp: number;
  characterLevel: number;
  petXp: number;
  feats: Record<ChallengeMetric, number>;
  newPets: string[];
}

/** Chance an already-anchored era still drops an item on repeat clears. */
const REPEAT_DROP_CHANCE = 0.25;

/**
 * Roll one loot item for a cleared era, deterministically from the run seed.
 * `guaranteed` = first-ever clear of this era (always rolls if any item is
 * unowned); otherwise a 25% chance to roll one unowned item. Items already in
 * `save.inventory` are excluded so drops never waste a roll on a dupe; if
 * every item in the table is owned, nothing is awarded. Empty loot tables
 * (content stubs) never roll.
 */
export function rollEraLoot(reg: ContentRegistry, save: SaveData, eraIndex: number, seed: number, guaranteed: boolean): string | null {
  const era = reg.eras[eraIndex];
  if (!era || era.loot.length === 0) return null;
  const rng = new Rng(seed ^ (eraIndex * 7919));
  if (!guaranteed && !rng.chance(REPEAT_DROP_CHANCE)) return null;
  const unowned = era.loot.filter((l) => !save.inventory.includes(l.itemId));
  if (unowned.length === 0) return null;
  const idx = rng.weightedIndex(unowned.map((l) => l.weight));
  return unowned[idx].itemId;
}

/**
 * Apply a finished run to the save: currency, anchors, stats. Returns the summary.
 * `player` is the local player's index (online co-op: each peer credits its own
 * profile using its own shard multiplier; run-level progress is shared).
 */
export function applyRunResult(save: SaveData, sim: Sim, player = 0): RunResult {
  const r = sim.run;
  const victory = r.phase === 'victory';
  const shards = shardReward(
    {
      erasCleared: r.erasCleared,
      bossesKilled: r.bossesKilled,
      timeSurvived: r.elapsed,
      kills: r.kills,
      noHitStages: r.noHitStages,
      paradox: r.paradox,
      startEra: r.startEra,
      victory,
      shardsPickedUp: r.shardsPickedUp,
    },
    sim.getStats(player).shardMult,
  );
  const score = runScore({ kills: r.kills, erasCleared: r.erasCleared, bossesKilled: r.bossesKilled, level: r.level, paradox: r.paradox, timeSurvived: r.elapsed });
  const highestEra = r.clearedEras.length > 0 ? Math.max(r.eraIndex, ...r.clearedEras.map((e) => e + 1)) : r.eraIndex;
  const character = sim.ctx.characters[player] ?? 'knight';
  const priorLevel = characterLevel(save.characters[character].xp);
  const highestLevel = Math.max(...Object.values(save.characters).map((progress) => characterLevel(progress.xp)));
  const catchUp = priorLevel + 5 < highestLevel ? 1.5 : 1;
  const anchorPenalty = Math.max(0.35, 0.6 ** r.startEra);
  const characterXp = Math.round((30 * r.erasCleared + 50 * r.bossesKilled + r.elapsed / 6 + r.kills / 10 + 10 * r.noHitStages) * paradoxDef(r.paradox).shardReward * (victory ? 1.5 : 1) * anchorPenalty * catchUp);
  save.characters[character].xp += characterXp;
  const activePet = sim.config.pets?.[player] ?? null;
  const petXp = activePet ? Math.round(characterXp * 0.35) : 0;
  if (activePet) save.pets.xp[activePet] = (save.pets.xp[activePet] ?? 0) + petXp;
  const newAnchors: number[] = [];
  const priorAnchors = new Set(save.anchors);
  const drops: string[] = [];
  // every era whose boss died this run becomes an anchor; roll loot per era first
  for (const e of r.clearedEras) {
    const guaranteed = !priorAnchors.has(e);
    const drop = rollEraLoot(sim.reg, save, e, r.seed, guaranteed);
    if (drop) {
      drops.push(drop);
      if (!save.inventory.includes(drop)) save.inventory.push(drop);
    }
    if (!save.anchors.includes(e)) {
      save.anchors.push(e);
      newAnchors.push(e);
    }
  }
  save.anchors.sort((a, b) => a - b);
  const newCharacters: CharacterId[] = [];
  for (const character of CHARACTERS) {
    if (character.unlockAfterEra >= 0 && save.anchors.includes(character.unlockAfterEra) && !save.unlockedCharacters.includes(character.id)) {
      save.unlockedCharacters.push(character.id);
      newCharacters.push(character.id);
    }
  }
  save.chronoshards += shards;
  save.stats.runs++;
  if (victory) {
    save.stats.wins++;
    if (save.stats.fastestClearSec === null || r.elapsed < save.stats.fastestClearSec) save.stats.fastestClearSec = r.elapsed;
  }
  save.stats.bestScore = Math.max(save.stats.bestScore, score);
  save.stats.bestEra = Math.max(save.stats.bestEra, highestEra);
  save.stats.totalKills += r.kills;
  save.stats.totalBosses += r.bossesKilled;
  save.stats.noHitStages += r.noHitStages;
  save.stats.totalShards += shards;
  save.stats.playtimeSec += r.elapsed;
  const feats: Record<ChallengeMetric, number> = {
    objectiveClears: r.objectiveClears,
    perfectDefenses: r.perfectDefenses,
    fastHunts: r.fastHunts,
    flawlessEscorts: r.flawlessEscorts,
    noHitBosses: r.noHitBosses,
  };
  for (const metric of Object.keys(feats) as ChallengeMetric[]) save.mastery[metric] += feats[metric];
  const implementedEras = sim.reg.eras.filter((era) => era.implemented).length;
  if (victory && r.startEra === 0 && r.erasCleared >= implementedEras && r.paradox > 0 && !save.mastery.paradoxWins.includes(r.paradox)) {
    save.mastery.paradoxWins.push(r.paradox);
    save.mastery.paradoxWins.sort((a, b) => a - b);
  }
  const newPets = claimEarnedPets(save);
  if (sim.config.challengeId) {
    const challenge = CHALLENGE_BY_ID.get(sim.config.challengeId);
    const prior = save.challenges[sim.config.challengeId] ?? 0;
    const completed = challenge ? challengeCompleted(challenge, r.erasCleared, feats) : false;
    const progress = challenge?.requirement && !completed ? Math.min(r.erasCleared, Math.max(0, challenge.erasToClear - 1)) : r.erasCleared;
    save.challenges[sim.config.challengeId] = Math.max(prior, progress);
    if (challenge && completed && prior < challenge.erasToClear) {
      save.challenges[sim.config.challengeId] = Math.max(save.challenges[sim.config.challengeId], challenge.erasToClear);
      save.chronoshards += challenge.rewardShards;
      save.stats.totalShards += challenge.rewardShards;
    }
  }
  return { shards, score, victory, erasCleared: r.erasCleared, bossesKilled: r.bossesKilled, kills: r.kills, level: r.level, timeSurvivedSec: r.elapsed, highestEra, newAnchors, newCharacters, drops, newAchievements: [], characterXp, characterLevel: characterLevel(save.characters[character].xp), petXp, feats, newPets };
}
